import { createTheme, Theme, getContrastRatio } from '@mui/material/styles';

export type AppThemeMode = 'light' | 'dark';

/**
 * The single bridge from the app's color tokens (`src/index.css`) to MUI.
 *
 * MUI needs literal colors to derive hover, light/dark and contrast text, so
 * each value below mirrors the token named beside it. `muiTheme.test.ts` reads
 * index.css and fails if they drift. Do not add colors here that have no token.
 */
export const MUI_TOKEN_VALUES = {
    dark: {
        accent: '#4ECDC4', // --color-accent
        accentHover: '#5ED9D1', // --color-accent-hover
        onAccent: '#0F172A', // --color-on-accent
        bgPrimary: '#0f172a', // --color-bg-primary
        bgSecondary: '#1e293b', // --color-bg-secondary
        textPrimary: 'rgba(255, 255, 255, 0.95)', // --color-text-primary
        textSecondary: 'rgba(255, 255, 255, 0.70)', // --color-text-secondary
        border: 'rgba(255, 255, 255, 0.08)', // --color-border
        statusActive: '#10B981', // --color-status-active
        statusTriage: '#F59E0B', // --color-status-triage
    },
    light: {
        accent: '#0D9488', // --color-accent
        accentHover: '#0F766E', // --color-accent-hover
        accentText: '#0F736C', // --color-accent-text
        onAccent: '#0F172A', // --color-on-accent
        onAccentHover: '#FFFFFF', // --color-on-accent-hover
        bgPrimary: '#F1F5F9', // --color-bg-primary
        bgCard: '#FFFFFF', // --color-bg-card
        textPrimary: '#0F172A', // --color-text-primary
        textSecondary: '#475569', // --color-text-secondary
        border: '#E2E8F0', // --color-border
        statusActive: '#059669', // --color-status-active
        statusTriage: '#A94F08', // --color-status-triage
    },
} as const;

/** Contained success/warning: keep hover and filled-Alert dark-mode fill at `main`. */
function statusPalette(main: string, preferredInk: string, fallbackInk: string) {
    const contrastText = getContrastRatio(preferredInk, main) >= 4.5 ? preferredInk : fallbackInk;
    return { main, dark: main, contrastText };
}

export function createAppMuiTheme(mode: AppThemeMode): Theme {
    const isDark = mode === 'dark';
    const dark = MUI_TOKEN_VALUES.dark;
    const light = MUI_TOKEN_VALUES.light;

    return createTheme({
        palette: {
            mode,
            // Pick black or white text by WCAG AA (4.5:1), not MUI's default 3:1.
            contrastThreshold: 4.5,
            primary: isDark
                ? { main: dark.accent, light: dark.accentHover, contrastText: dark.onAccent }
                // Teal-600 is 3.7:1 as text on white. MUI uses primary.main as text
                // (text buttons, focused labels on recessed fields), so light mode
                // leads with the accent-text shade, which passes there and under white.
                : { main: light.accentText, dark: light.accentText, contrastText: light.onAccentHover },
            background: isDark
                ? { default: dark.bgPrimary, paper: dark.bgSecondary }
                : { default: light.bgPrimary, paper: light.bgCard },
            text: isDark
                ? { primary: dark.textPrimary, secondary: dark.textSecondary }
                : { primary: light.textPrimary, secondary: light.textSecondary },
            divider: isDark ? dark.border : light.border,
            // Mark complete / Reopen use contained success / warning. MUI's default
            // `darken(main, 0.3)` is the hover and filled-Alert (dark mode) fill;
            // navy ink fails 4.5:1 there, so `dark` stays `main`. Ink is
            // --color-on-accent when that pair passes, else --color-on-accent-hover
            // (white in light) so a darker triage token still reads.
            success: isDark
                ? statusPalette(dark.statusActive, dark.onAccent, dark.onAccent)
                : statusPalette(light.statusActive, light.onAccent, light.onAccentHover),
            warning: isDark
                ? statusPalette(dark.statusTriage, dark.onAccent, dark.onAccent)
                : statusPalette(light.statusTriage, light.onAccent, light.onAccentHover),
        },
        components: {
            MuiPaper: {
                styleOverrides: {
                    // Dark-mode elevation overlays would lighten surfaces that set
                    // their own token background.
                    root: { backgroundImage: 'none' },
                },
            },
        },
    });
}
